export const ONBOARDING_STATUS = {
  NEEDS_ROLE: 'needs_role',
  NEEDS_PROFILE: 'needs_profile',
  NEEDS_INSTAGRAM: 'needs_instagram',
  PENDING_ADMIN: 'pending_admin',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  INVITED_ACCESS: 'invited_access',
};

export const getUserProfileForRole = (user) => {
  if (!user) return null;
  if (user.user_type === 'business') return user.business_profile;
  if (user.user_type === 'creator') return user.creator_profile;
  return null;
};

export const getOnboardingStatus = (user) => {
  if (!user) return ONBOARDING_STATUS.NEEDS_ROLE;
  if (user.onboarding_status) return user.onboarding_status;

  if (user.is_staff || user.is_superuser) return ONBOARDING_STATUS.APPROVED;
  if (user.user_type === 'none') return ONBOARDING_STATUS.NEEDS_ROLE;
  if (user.user_type === 'seller') {
    return user.is_profile_completed ? ONBOARDING_STATUS.APPROVED : ONBOARDING_STATUS.NEEDS_PROFILE;
  }

  const profile = getUserProfileForRole(user);
  if (!profile) return ONBOARDING_STATUS.NEEDS_PROFILE;
  if (profile.admin_verified && profile.verification_status === 'approved') return ONBOARDING_STATUS.APPROVED;
  const hasInvitedAccess = user.user_type === 'creator'
    && (
      profile.has_invited_access
      || profile.invited_offer_ids?.length > 0
      || profile.invited_collab
    );
  if (hasInvitedAccess) return ONBOARDING_STATUS.INVITED_ACCESS;
  if (profile.verification_status === 'rejected') return ONBOARDING_STATUS.REJECTED;
  if (!user.is_profile_completed) return ONBOARDING_STATUS.NEEDS_PROFILE;
  const hasManualBusinessVerification = user.user_type === 'business' && profile.ruc_tax_id && profile.id_document;
  if (!profile.instagram_account_connected && !hasManualBusinessVerification) return ONBOARDING_STATUS.NEEDS_INSTAGRAM;
  return ONBOARDING_STATUS.PENDING_ADMIN;
};

export const hasInvitedCreatorAccess = (user) => {
  const profile = user?.creator_profile;
  return user?.user_type === 'creator'
    && Boolean(
      profile?.has_invited_access
      || profile?.invited_offer_ids?.length > 0
      || profile?.invited_collab
    );
};

export const getFullOnboardingStatus = (user) => {
  if (!user) return ONBOARDING_STATUS.NEEDS_ROLE;
  if (user.full_onboarding_status) return user.full_onboarding_status;

  if (user.is_staff || user.is_superuser) return ONBOARDING_STATUS.APPROVED;
  if (user.user_type === 'none') return ONBOARDING_STATUS.NEEDS_ROLE;
  if (user.user_type === 'seller') {
    return user.is_profile_completed ? ONBOARDING_STATUS.APPROVED : ONBOARDING_STATUS.NEEDS_PROFILE;
  }

  const profile = getUserProfileForRole(user);
  if (!profile) return ONBOARDING_STATUS.NEEDS_PROFILE;
  if (profile.admin_verified && profile.verification_status === 'approved') return ONBOARDING_STATUS.APPROVED;
  if (profile.verification_status === 'rejected') return ONBOARDING_STATUS.REJECTED;
  if (!user.is_profile_completed) return ONBOARDING_STATUS.NEEDS_PROFILE;
  const hasManualBusinessVerification = user.user_type === 'business' && profile.ruc_tax_id && profile.id_document;
  if (!profile.instagram_account_connected && !hasManualBusinessVerification) return ONBOARDING_STATUS.NEEDS_INSTAGRAM;
  return ONBOARDING_STATUS.PENDING_ADMIN;
};

export const getInvitedCreatorPath = (user) => {
  const profile = user?.creator_profile;
  const invitedOfferId = profile?.invited_offer_ids?.[0] || profile?.invited_collab;
  return invitedOfferId ? `/creator/invited/${invitedOfferId}` : '/creator/invited';
};

export const getDefaultRouteForUser = (user, options = {}) => {
  const {
    nextParam = '',
    returnTo = '',
    inviteToken = '',
    partnerCode = '',
    preferActiveApplications = false,
  } = options;

  if (nextParam?.startsWith('/')) return nextParam;
  if (returnTo?.startsWith('/')) return returnTo;

  if (inviteToken && user?.user_type === 'creator') {
    const invitedOfferId = user?.creator_profile?.invited_offer_ids?.[0] || user?.creator_profile?.invited_collab;
    return invitedOfferId ? `/creator/invited/${invitedOfferId}` : `/i/${inviteToken}`;
  }

  if (user?.is_staff || user?.is_superuser) return '/console/dashboard';
  if (user?.user_type === 'seller') return '/seller/dashboard';

  const status = getOnboardingStatus(user);

  if (status === ONBOARDING_STATUS.NEEDS_ROLE) return '/select-role';
  if (status === ONBOARDING_STATUS.NEEDS_PROFILE || status === ONBOARDING_STATUS.NEEDS_INSTAGRAM) {
    if (partnerCode && user?.user_type === 'business') return '/business/payment/plans';
    return '/complete-profile';
  }
  if (status === ONBOARDING_STATUS.APPROVED) {
    if (preferActiveApplications && user?.user_type === 'creator' && user?.has_active_applications) {
      return '/creator/applications';
    }
    if (user?.user_type === 'business') {
      const profile = user?.business_profile;
      const isFirstCollab = profile && (profile.is_first_collab || profile.has_created_collabs === false || profile.collabs_count === 0);
      return isFirstCollab ? '/business/first-collab' : '/business/collabs';
    }
    return '/creator/feed';
  }
  if (status === ONBOARDING_STATUS.INVITED_ACCESS) return getInvitedCreatorPath(user);
  if (user?.user_type === 'business') return '/business/pending-approval';
  if (user?.user_type === 'creator') return '/creator/pending-approval';

  return '/dashboard';
};

export const isPendingApprovalRouteAllowed = (pathname = '') => (
  pathname.startsWith('/creator/pending-approval')
  || pathname.startsWith('/business/pending-approval')
);
